import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  DocumentSignBulkBatchResponse,
  DocumentSignBulkRecipient,
  DocumentSignSendMode,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { addBreadcrumb, traceEnd, traceError, traceStart } from '@/lib/telemetry';

/**
 * Switch a draft between one shared envelope and a bulk send. The server
 * carries recipients and placed fields across, so every cached view of the
 * envelope's children is stale afterwards.
 */
export function useSetSendMode(envelopeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sendMode: DocumentSignSendMode) => {
      const result = await api.setDocumentSignSendMode(envelopeId, sendMode);
      // Refresh the recipient rows BEFORE the envelope. The builder remounts
      // the recipients table when the envelope's mode changes, and the table
      // hydrates once from whatever is cached at that moment. Hydrating from
      // the pre-switch rows (the nameless placeholder) and then autosaving
      // would delete the people the switch had just converted.
      await queryClient.refetchQueries({
        queryKey: queryKeys.documentSign.recipients(envelopeId),
        type: 'active',
      });
      return result.envelope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}

/** Replace a bulk envelope's list of people. */
export function useSetBulkRecipients(envelopeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (recipients: DocumentSignBulkRecipient[]) => {
      const result = await api.setDocumentSignBulkRecipients(envelopeId, { recipients });
      return result.recipients;
    },
    onSuccess: () => {
      // Only the envelope itself carries the list. `exact` keeps an autosave
      // from refetching the files, fields, recipients and events nested
      // under the envelope's key — none of which a list edit changes.
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentSign.envelope(envelopeId),
        exact: true,
      });
    },
  });
}

/**
 * Start a bulk send. The template is retired by the server the moment the
 * send is recorded, so its cached copy is dropped — otherwise browser Back to
 * the review page would show a sendable draft that no longer exists.
 */
export function useBulkSend(envelopeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** `reviewFingerprint`: the envelope as the owner was shown it. */
    mutationFn: async (reviewFingerprint: string) => {
      const traceId = traceStart('document-sign:envelope-bulk-send', { envelopeId });
      try {
        const result = await api.bulkSendDocumentSignEnvelope(envelopeId, reviewFingerprint);
        traceEnd(traceId, { total: result.total });
        addBreadcrumb('document-sign', 'Bulk send started', { envelopeId });
        return result;
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
      // The new send joins the list of sends (every workspace's, by prefix).
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.bulkBatchLists() });
    },
    // Refused because the envelope changed since review: show the current one.
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentSign.envelope(envelopeId),
        exact: true,
      });
    },
  });
}

/** How often the overview refreshes while copies are still being made. */
const BULK_SEND_ACTIVE_POLL_MS = 3_000;
/** …and while copies are out for signature, waiting on people. */
const BULK_SEND_WAITING_POLL_MS = 30_000;

/**
 * How often to refresh a bulk send's overview: quickly while copies are being
 * made, slowly while copies are out waiting on people, and not at all once
 * nothing can change.
 */
export function bulkBatchPollInterval(
  batch: DocumentSignBulkBatchResponse | undefined,
): number | false {
  if (!batch) return false;
  if (batch.status === 'processing') return BULK_SEND_ACTIVE_POLL_MS;
  const waiting = batch.items.some(
    (i) => i.envelopeStatus === 'sent' || i.envelopeStatus === 'in_progress',
  );
  return waiting ? BULK_SEND_WAITING_POLL_MS : false;
}

/** One bulk send and where each person stands — refreshed while it moves. */
export function useBulkBatch(batchId: string | undefined) {
  const { data, isLoading, error } = useQuery({
    // Disabled without an id; the key is never fetched then.
    queryKey: queryKeys.documentSign.bulkBatch(batchId ?? ''),
    queryFn: () => api.getDocumentSignBulkBatch(batchId!),
    enabled: !!batchId,
    refetchInterval: (query) => bulkBatchPollInterval(query.state.data),
    refetchIntervalInBackground: false,
  });
  return {
    batch: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load bulk send' : null,
  };
}

/**
 * The user's own bulk sends in a workspace, newest first, a page at a time —
 * the way back to a send's overview that needs none of its copies to exist.
 * `loadOlder` fetches the next page, so every send stays reachable. Refreshes
 * while any loaded send is still going.
 */
export function useBulkBatches(holderId: string | null | undefined) {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.documentSign.bulkBatches(holderId ?? ''),
      queryFn: ({ pageParam }) => api.listDocumentSignBulkBatches(holderId!, pageParam),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: !!holderId,
      refetchInterval: (query) =>
        query.state.data?.pages.some((p) => p.bulkSends.some((b) => b.status === 'processing'))
          ? 15_000
          : false,
      refetchIntervalInBackground: false,
    });
  return {
    bulkSends: data?.pages.flatMap((p) => p.bulkSends) ?? [],
    hasOlder: hasNextPage,
    loadingOlder: isFetchingNextPage,
    loadOlder: () => void fetchNextPage(),
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load bulk sends' : null,
  };
}

/** Which optional features this deployment has switched on. Off until known. */
export function useDocumentSignFeatures() {
  const { data } = useQuery({
    queryKey: queryKeys.documentSign.features(),
    queryFn: () => api.getDocumentSignFeatures(),
    staleTime: 60_000,
  });
  return { bulkSend: data?.bulkSend === true, sendFeeCredits: data?.sendFeeCredits ?? null };
}
